import React, { useEffect, useMemo, useState } from "react";
import { format, parse, startOfWeek, endOfWeek, addWeeks } from "date-fns";
import { Send, X, Loader2, Search, ChevronDown, AlertTriangle } from "lucide-react";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import type { ScheduleV3Group, ScheduleV3Schedule } from "../../../../types";
import type { PublishScheduleV3Request, PublishScheduleScope } from "../../../../types";
import { buildConflictIndex } from "../../schedulingToolConflictUtils";
import {
    DEFAULT_PUBLISH_SCOPE,
    type PublishScopeConfig,
} from "../../../../utils/schedulingToolConfig";

export interface PublishScheduleModalProps {
    open: boolean;
    onClose: () => void;
    defaultStartDate: string;
    defaultEndDate: string;
    groups: ScheduleV3Group[];
    departmentOptions: { value: string; label: string }[];
    siteOptions: { value: string; label: string }[];
    loadEmployeeOptions: (inputValue: string) => Promise<{ value: number; label: string }[]>;
    onPublish: (payload: PublishScheduleV3Request, conflictCount: number) => void;
    isPublishing: boolean;
    /** Account-level PUBLISH_SCOPE; only flags with true/1 are shown */
    publishScope?: PublishScopeConfig;
}

const SCOPE_OPTIONS: { value: PublishScheduleScope; label: string; configKey: keyof PublishScopeConfig }[] = [
    { value: "ALL", label: "All schedules", configKey: "all" },
    { value: "SELF_CREATED", label: "Only schedules I created", configKey: "created_by_me" },
    { value: "EMPLOYEES", label: "Specific employees", configKey: "employees" },
    { value: "DEPARTMENT", label: "By department", configKey: "department" },
    { value: "SITE", label: "By site", configKey: "site" },
];

function getEnabledScopeOptions(publishScope: PublishScopeConfig) {
    const enabled = SCOPE_OPTIONS.filter((opt) => publishScope[opt.configKey]);
    return enabled.length > 0 ? enabled : SCOPE_OPTIONS.filter((opt) => opt.value === "SELF_CREATED");
}

function getDefaultScope(publishScope: PublishScopeConfig): PublishScheduleScope {
    const enabled = getEnabledScopeOptions(publishScope);
    const preferred = enabled.find((opt) => opt.value === "SELF_CREATED");
    return (preferred ?? enabled[0]).value;
}

function isUnpublished(schedule: ScheduleV3Schedule): boolean {
    const p = schedule.publish;
    if (p === null || p === undefined) return true;
    const n = typeof p === "string" ? parseInt(p, 10) : p;
    return n === 0 || Number.isNaN(n);
}

function getUnpublishedCount(
    groups: ScheduleV3Group[],
    startDateStr: string,
    endDateStr: string,
    scope: PublishScheduleScope,
    employeeIds: number[],
    departmentCodes: string[],
    siteIds: string[]
): number {
    let count = 0;
    const start = parse(startDateStr, "yyyy-MM-dd", new Date()).getTime();
    const end = parse(endDateStr, "yyyy-MM-dd", new Date()).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || start > end) return 0;

    const employeeIdSet = new Set(employeeIds.map(String));
    const deptSet = new Set(departmentCodes);
    const siteSet = new Set(siteIds);

    for (const group of groups) {
        if (!group.dates) continue;
        for (const dateStr of Object.keys(group.dates)) {
            const dateMs = parse(dateStr, "yyyy-MM-dd", new Date()).getTime();
            if (Number.isNaN(dateMs) || dateMs < start || dateMs > end) continue;
            const dateData = group.dates[dateStr];
            if (!dateData?.schedules) continue;
            for (const schedule of dateData.schedules) {
                if (!isUnpublished(schedule)) continue;
                if (scope === "EMPLOYEES" && employeeIds.length > 0 && !employeeIdSet.has(String(schedule.employee?.id))) continue;
                if (scope === "DEPARTMENT" && departmentCodes.length > 0 && !deptSet.has(group.group_id)) continue;
                if (scope === "SITE" && siteIds.length > 0) {
                    const scheduleSiteId = schedule.site?.site_id != null ? String(schedule.site.site_id) : group.group_id;
                    if (!siteSet.has(scheduleSiteId) && !siteSet.has(group.group_id)) continue;
                }
                count++;
            }
        }
    }
    return count;
}

const PublishScheduleModal: React.FC<PublishScheduleModalProps> = ({
    open,
    onClose,
    defaultStartDate,
    defaultEndDate,
    groups,
    departmentOptions,
    siteOptions,
    loadEmployeeOptions,
    onPublish,
    isPublishing,
    publishScope = DEFAULT_PUBLISH_SCOPE,
}) => {
    const enabledScopeOptions = useMemo(() => getEnabledScopeOptions(publishScope), [publishScope]);
    const defaultScope = useMemo(() => getDefaultScope(publishScope), [publishScope]);

    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [scope, setScope] = useState<PublishScheduleScope>(defaultScope);
    const [employeeOptions, setEmployeeOptions] = useState<{ value: number; label: string }[]>([]);
    const [departmentCodes, setDepartmentCodes] = useState<string[]>([]);
    const [siteIds, setSiteIds] = useState<string[]>([]);
    const [publishNote, setPublishNote] = useState("");
    const [sendNotification, setSendNotification] = useState(true);
    const [generateIcs, setGenerateIcs] = useState(false);

    useEffect(() => {
        if (open) {
            setStartDate(defaultStartDate);
            setEndDate(defaultEndDate);
            setScope(defaultScope);
        }
    }, [open, defaultStartDate, defaultEndDate, defaultScope]);

    const unpublishedCount = useMemo(() => {
        return getUnpublishedCount(groups, startDate, endDate, scope, employeeOptions.map((e) => e.value), departmentCodes, siteIds);
    }, [groups, startDate, endDate, scope, employeeOptions, departmentCodes, siteIds]);

    const conflictCount = useMemo(() => {
        if (!startDate || !endDate) return 0;
        const start = parse(startDate, "yyyy-MM-dd", new Date()).getTime();
        const end = parse(endDate, "yyyy-MM-dd", new Date()).getTime();
        if (Number.isNaN(start) || Number.isNaN(end) || start > end) return 0;
        const conflictIndex = buildConflictIndex(groups);
        const employeeIdSet = new Set(employeeOptions.map((e) => String(e.value)));
        const deptSet = new Set(departmentCodes);
        const siteSet = new Set(siteIds);
        let count = 0;
        for (const group of groups) {
            if (!group.dates) continue;
            for (const dateStr of Object.keys(group.dates)) {
                const dateMs = parse(dateStr, "yyyy-MM-dd", new Date()).getTime();
                if (Number.isNaN(dateMs) || dateMs < start || dateMs > end) continue;
                const dateData = group.dates[dateStr];
                if (!dateData?.schedules) continue;
                for (const schedule of dateData.schedules) {
                    if (!isUnpublished(schedule)) continue;
                    if (scope === "EMPLOYEES" && employeeOptions.length > 0 && !employeeIdSet.has(String(schedule.employee?.id))) continue;
                    if (scope === "DEPARTMENT" && departmentCodes.length > 0 && !deptSet.has(group.group_id)) continue;
                    if (scope === "SITE" && siteIds.length > 0) {
                        const scheduleSiteId = schedule.site?.site_id != null ? String(schedule.site.site_id) : group.group_id;
                        if (!siteSet.has(scheduleSiteId) && !siteSet.has(group.group_id)) continue;
                    }
                    if (schedule.os_emp_schedules_id != null && conflictIndex.has(schedule.os_emp_schedules_id)) {
                        count++;
                    }
                }
            }
        }
        return count;
    }, [groups, startDate, endDate, scope, employeeOptions, departmentCodes, siteIds]);

    const summaryStartDisplay = startDate ? format(parse(startDate, "yyyy-MM-dd", new Date()), "MMM d") : "";
    const summaryEndDisplay = endDate ? format(parse(endDate, "yyyy-MM-dd", new Date()), "MMM d") : "";
    const summaryRange = summaryStartDisplay && summaryEndDisplay ? `${summaryStartDisplay} – ${summaryEndDisplay}` : "";

    const canPublish =
        startDate &&
        endDate &&
        (scope !== "EMPLOYEES" || employeeOptions.length > 0) &&
        (scope !== "DEPARTMENT" || departmentCodes.length > 0) &&
        (scope !== "SITE" || siteIds.length > 0);

    const handleThisWeek = () => {
        const now = new Date();
        const start = startOfWeek(now, { weekStartsOn: 0 });
        const end = endOfWeek(now, { weekStartsOn: 0 });
        setStartDate(format(start, "yyyy-MM-dd"));
        setEndDate(format(end, "yyyy-MM-dd"));
    };

    const handleNextWeek = () => {
        const now = new Date();
        const nextWeekStart = addWeeks(startOfWeek(now, { weekStartsOn: 0 }), 1);
        const nextWeekEnd = endOfWeek(nextWeekStart, { weekStartsOn: 0 });
        setStartDate(format(nextWeekStart, "yyyy-MM-dd"));
        setEndDate(format(nextWeekEnd, "yyyy-MM-dd"));
    };

    const isThisWeekSelected = useMemo(() => {
        if (!startDate || !endDate) return false;
        const now = new Date();
        const start = startOfWeek(now, { weekStartsOn: 0 });
        const end = endOfWeek(now, { weekStartsOn: 0 });
        const currentStart = parse(startDate, "yyyy-MM-dd", new Date());
        const currentEnd = parse(endDate, "yyyy-MM-dd", new Date());
        return currentStart.getTime() === start.getTime() && currentEnd.getTime() === end.getTime();
    }, [startDate, endDate]);

    const isNextWeekSelected = useMemo(() => {
        if (!startDate || !endDate) return false;
        const now = new Date();
        const nextStart = addWeeks(startOfWeek(now, { weekStartsOn: 0 }), 1);
        const nextEnd = endOfWeek(nextStart, { weekStartsOn: 0 });
        const currentStart = parse(startDate, "yyyy-MM-dd", new Date());
        const currentEnd = parse(endDate, "yyyy-MM-dd", new Date());
        return currentStart.getTime() === nextStart.getTime() && currentEnd.getTime() === nextEnd.getTime();
    }, [startDate, endDate]);

    const handlePublish = () => {
        if (!canPublish) return;
        const payload: PublishScheduleV3Request = {
            start_date: startDate,
            end_date: endDate,
            scope,
            employee_ids: scope === "EMPLOYEES" ? employeeOptions.map((e) => Number(e.value)) : undefined,
            department_codes: scope === "DEPARTMENT" ? departmentCodes : undefined,
            site_ids: scope === "SITE" ? siteIds : undefined,
            publish_note: publishNote.trim() || undefined,
            send_notification: sendNotification,
            generate_ics: generateIcs,
        };
        onPublish(payload, conflictCount);
    };

    const handleClose = () => {
        setEmployeeOptions([]);
        setDepartmentCodes([]);
        setSiteIds([]);
        setPublishNote("");
        setSendNotification(true);
        setGenerateIcs(false);
        setScope(defaultScope);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1400] p-2 md:p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-2 md:mx-0 max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2 md:gap-3 min-w-0">
                        <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex-shrink-0">
                            <Send className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-sm md:text-lg font-bold text-gray-900 truncate">Publish Schedules</h3>
                            <p className="text-xs text-gray-500 truncate">Publish schedules for selected criteria.</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                    >
                        <X size={18} className="md:w-5 md:h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-4 md:px-6 py-3 md:py-4 space-y-0 overflow-y-auto flex-1">
                    {/* 1. Date Range */}
                    <div className="pb-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-semibold">1</span>
                                <h4 className="text-sm font-bold text-gray-800">Date Range</h4>
                            </div>
                            <div className="inline-flex rounded-lg border border-blue-200 overflow-hidden">
                                <button
                                    type="button"
                                    onClick={handleThisWeek}
                                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                                        isThisWeekSelected
                                            ? "bg-blue-600 text-white"
                                            : "bg-white text-blue-600 hover:bg-blue-50"
                                    }`}
                                >
                                    This Week
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNextWeek}
                                    className={`px-3 py-1.5 text-xs font-medium border-l border-blue-200 transition-colors ${
                                        isNextWeekSelected
                                            ? "bg-blue-600 text-white"
                                            : "bg-white text-blue-600 hover:bg-blue-50"
                                    }`}
                                >
                                    Next Week
                                </button>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div>

                    <hr className="border-gray-200" />

                    {/* 2. Scope */}
                    <div className="py-4">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-semibold">2</span>
                            <h4 className="text-sm font-bold text-gray-800">Scope</h4>
                        </div>
                        <div className="space-y-2">
                            {enabledScopeOptions.map((opt) => (
                                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="scope"
                                        value={opt.value}
                                        checked={scope === opt.value}
                                        onChange={() => setScope(opt.value)}
                                        className="text-green-600 border-gray-300 focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{opt.label}</span>
                                </label>
                            ))}
                        </div>
                        {scope === "EMPLOYEES" && (
                            <div className="mt-3 space-y-2">
                                {employeeOptions.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {employeeOptions.map((emp, idx) => {
                                            const parts = emp.label.trim().split(/\s+/);
                                            const initials = parts.length >= 2
                                                ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
                                                : emp.label.slice(0, 2).toUpperCase();
                                            const tagColor = idx % 2 === 0 ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800";
                                            return (
                                                <span
                                                    key={emp.value}
                                                    className={`inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md ${tagColor} text-sm font-medium`}
                                                >
                                                    <span className="font-semibold">{initials}</span>
                                                    <span>{emp.label}</span>
                                                    <ChevronDown className="w-4 h-4 opacity-70" />
                                                    <button
                                                        type="button"
                                                        onClick={() => setEmployeeOptions((prev) => prev.filter((e) => e.value !== emp.value))}
                                                        className="p-0.5 rounded hover:bg-black/10"
                                                        aria-label="Remove"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                                <div className="relative">
                                    <AsyncSelect
                                        cacheOptions
                                        value={null}
                                        onChange={(selected: { value: number; label: string } | null) => {
                                            if (selected && !employeeOptions.some((e) => e.value === selected.value)) {
                                                setEmployeeOptions((prev) => [...prev, selected]);
                                            }
                                        }}
                                        loadOptions={loadEmployeeOptions}
                                        placeholder="Search employees..."
                                        noOptionsMessage={({ inputValue }) =>
                                            inputValue.length < 3 ? "Type at least 3 characters to search" : "No employees found"
                                        }
                                        className="text-sm"
                                        classNamePrefix="react-select"
                                        isClearable
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 1500 }),
                                            menu: (base) => ({ ...base, zIndex: 1500 }),
                                            control: (base) => ({ ...base, paddingRight: 36 }),
                                        }}
                                    />
                                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                        )}
                        {scope === "DEPARTMENT" && (
                            <div className="mt-3">
                                <Select
                                    isMulti
                                    value={departmentOptions.filter((opt) => departmentCodes.includes(opt.value))}
                                    onChange={(selected) => setDepartmentCodes((selected || []).map((s: { value: string }) => s.value))}
                                    options={departmentOptions}
                                    className="text-sm"
                                    classNamePrefix="react-select"
                                    placeholder="Select departments..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 1500 }),
                                        menu: (base) => ({ ...base, zIndex: 1500 }),
                                    }}
                                />
                            </div>
                        )}
                        {scope === "SITE" && (
                            <div className="mt-3">
                                <Select
                                    isMulti
                                    value={siteOptions.filter((opt) => siteIds.includes(opt.value))}
                                    onChange={(selected) => setSiteIds((selected || []).map((s: { value: string }) => s.value))}
                                    options={siteOptions}
                                    className="text-sm"
                                    classNamePrefix="react-select"
                                    placeholder="Select sites..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 1500 }),
                                        menu: (base) => ({ ...base, zIndex: 1500 }),
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    <hr className="border-gray-200" />

                    {/* 3. Options */}
                    <div className="py-4">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-semibold">3</span>
                            <h4 className="text-sm font-bold text-gray-800">Options</h4>
                        </div>
                        <div className="space-y-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={sendNotification}
                                    onChange={(e) => setSendNotification(e.target.checked)}
                                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                                <span className="text-sm text-gray-700">Send notification to employees</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={generateIcs}
                                    onChange={(e) => setGenerateIcs(e.target.checked)}
                                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                                <span className="text-sm text-gray-700">Generate calendar (.ics) file</span>
                            </label>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Publish Note</label>
                                <textarea
                                    value={publishNote}
                                    onChange={(e) => setPublishNote(e.target.value)}
                                    placeholder="Optional message to employees..."
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent resize-y"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Summary */}
                    <div className="space-y-2">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                            <Search className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-blue-800">
                                {summaryRange ? (
                                    <>
                                        Will publish <strong>{unpublishedCount}</strong> unpublished schedule{unpublishedCount !== 1 ? "s" : ""} for {summaryRange}
                                    </>
                                ) : (
                                    "Select a date range to see how many schedules will be published."
                                )}
                            </p>
                        </div>
                        {conflictCount > 0 && (
                            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2">
                                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-amber-800">
                                    <strong>{conflictCount}</strong> of these schedule{conflictCount !== 1 ? "s have" : " has"} conflicts (overlap, time off, unavailability, or holiday). Publishing will proceed, but review may be needed.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-4 md:px-6 py-3 md:py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex justify-end gap-2 md:gap-3 flex-shrink-0">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 md:px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handlePublish}
                        disabled={!canPublish || isPublishing}
                        className="px-5 md:px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                    >
                        {isPublishing ? (
                            <>
                                <Loader2 className="animate-spin" size={16} />
                                Publishing...
                            </>
                        ) : (
                            <>
                                <Send size={16} />
                                Publish Now
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PublishScheduleModal;
